/**
 * Centralized logging utility for frontend.
 * 
 * SECURITY: Only logs in development mode to prevent information leakage in production.
 * Never logs sensitive data (tokens, passwords, API keys, user emails).
 */

const isDev = import.meta.env.DEV || import.meta.env.VITE_DEBUG_MODE === 'true'

/**
 * Check if a value contains sensitive information that should not be logged.
 */
function containsSensitiveData(value: any): boolean {
  if (typeof value !== 'string') {
    return false
  }
  
  const sensitivePatterns = [
    /token/i,
    /password/i,
    /secret/i,
    /key/i,
    /auth/i,
    /bearer/i,
    /@.*\./i, // Email addresses
    /sk-[a-zA-Z0-9-]+/i, // API keys (OpenAI format)
    /sk-ant-[a-zA-Z0-9-]+/i, // API keys (Anthropic format)
  ]
  
  return sensitivePatterns.some(pattern => pattern.test(value))
}

/**
 * Sanitize data before logging by removing sensitive information.
 */
function sanitizeData(data: any): any {
  if (data === null || data === undefined) {
    return data
  }
  
  if (typeof data === 'string') {
    if (containsSensitiveData(data)) {
      return '[REDACTED]'
    }
    return data
  }
  
  if (Array.isArray(data)) {
    return data.map(sanitizeData)
  }
  
  if (typeof data === 'object') {
    const sanitized: any = {}
    for (const [key, value] of Object.entries(data)) {
      // Skip sensitive keys
      if (containsSensitiveData(key) || containsSensitiveData(value)) {
        sanitized[key] = '[REDACTED]'
      } else {
        sanitized[key] = sanitizeData(value)
      }
    }
    return sanitized
  }
  
  return data
}

/**
 * Log a message (only in development mode).
 */
export function log(message: string, ...args: any[]): void {
  if (isDev) {
    const sanitized = args.map(sanitizeData)
    console.log(`[${new Date().toISOString()}] ${message}`, ...sanitized)
  }
}

/**
 * Send error log to backend server (production only).
 * Fails silently to avoid breaking the app if logging fails.
 */
async function sendErrorToServer(level: string, message: string, error?: any): Promise<void> {
  if (isDev) {
    return // Only send to server in production
  }

  try {
    // Determine API base URL (similar to API client logic)
    let baseURL = import.meta.env.VITE_API_URL || ''
    const currentOrigin = window.location.origin
    
    // Map frontend domains to their corresponding API domains
    // NOTE: Dev frontend now uses production API to avoid cookie conflicts
    const domainMapping: Record<string, string> = {
      'https://scidraft.com': 'https://api.scidraft.com',
      'https://www.scidraft.com': 'https://api.scidraft.com',
      'https://app.scidraft.com': 'https://api.scidraft.com',
      'https://dev.scidraft.com': 'https://api.scidraft.com',  // Dev frontend uses production API
      'https://staging.scidraft.com': 'https://api-staging.scidraft.com',
    }
    
    if (domainMapping[currentOrigin]) {
      baseURL = domainMapping[currentOrigin]
    } else if (!baseURL) {
      // Use relative path if no explicit API URL
      baseURL = ''
    }
    
    const apiEndpoint = baseURL ? `${baseURL}/api/logging/frontend-error` : '/api/logging/frontend-error'
    
    // Build error details
    let errorDetails: any = null
    let stackTrace: string | null = null
    
    if (error) {
      const sanitized = sanitizeData(error)
      if (typeof sanitized === 'object') {
        errorDetails = sanitized
      } else {
        errorDetails = { message: String(sanitized) }
      }
      
      // Extract stack trace if available
      if (error instanceof Error && error.stack) {
        stackTrace = error.stack
      } else if (error?.stack) {
        stackTrace = String(error.stack)
      }
    }
    
    // Send to backend (fire and forget - don't await)
    fetch(apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies for auth
      body: JSON.stringify({
        level,
        message,
        error_details: errorDetails,
        stack_trace: stackTrace,
        url: window.location.href,
        user_agent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      }),
    }).catch(() => {
      // Silently fail - don't break the app if logging fails
    })
  } catch {
    // Silently fail - don't break the app if logging fails
  }
}

/**
 * Log an error (always logged, but sanitized).
 * In production, also sends to backend server for centralized logging.
 */
export function logError(message: string, error?: any): void {
  if (isDev) {
    console.error(`[${new Date().toISOString()}] ${message}`, error ? sanitizeData(error) : '')
  } else {
    // In production, send to backend server for centralized logging
    sendErrorToServer('error', message, error)
  }
}

/**
 * Log a warning (only in development mode).
 */
export function logWarn(message: string, ...args: any[]): void {
  if (isDev) {
    const sanitized = args.map(sanitizeData)
    console.warn(`[${new Date().toISOString()}] ${message}`, ...sanitized)
  }
}

/**
 * Log debug information (only in development mode).
 */
export function logDebug(message: string, ...args: any[]): void {
  if (isDev && import.meta.env.VITE_DEBUG_MODE === 'true') {
    const sanitized = args.map(sanitizeData)
    console.debug(`[${new Date().toISOString()}] ${message}`, ...sanitized)
  }
}

